package com.karyadina.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.QrCode
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.animation.core.*
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import com.karyadina.common.generated.resources.Res
import com.karyadina.common.generated.resources.bmc_button
import com.karyadina.common.generated.resources.support_me_on_kofi_badge_beige
import com.karyadina.common.generated.resources.bhim_upi_
import com.karyadina.common.generated.resources.support_dev_title
import com.karyadina.common.generated.resources.support_dev_desc
import com.karyadina.common.generated.resources.support_dev_upi
import com.karyadina.common.generated.resources.support_dev_show_qr
import com.karyadina.common.generated.resources.support_dev_scan_qr
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.platform.LocalUriHandler
import com.karyadina.config.DonationConfig

@Composable
fun DonationCard(
    modifier: Modifier = Modifier
) {
    val uriHandler = LocalUriHandler.current
    var showQrCodes by remember { mutableStateOf(false) }
    var selectedQrIndex by remember { mutableStateOf(0) }
    
    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.7f),
                            MaterialTheme.colorScheme.surface
                        )
                    )
                )
                .padding(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            val infiniteTransition = rememberInfiniteTransition()
            val heartScale by infiniteTransition.animateFloat(
                initialValue = 1f,
                targetValue = 1.15f,
                animationSpec = infiniteRepeatable(
                    animation = tween(800, easing = FastOutSlowInEasing),
                    repeatMode = RepeatMode.Reverse
                )
            )

            Icon(
                imageVector = Icons.Filled.Favorite,
                contentDescription = null,
                tint = Color(0xFFE91E63),
                modifier = Modifier.size(48.dp).graphicsLayer {
                    scaleX = heartScale
                    scaleY = heartScale
                }
            )
            
            Spacer(modifier = Modifier.height(12.dp))
            
            Text(
                text = stringResource(Res.string.support_dev_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            
            Spacer(modifier = Modifier.height(8.dp))
            
            Text(
                text = stringResource(Res.string.support_dev_desc),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center
            )
            
            Spacer(modifier = Modifier.height(20.dp))
            
            // 1. UPI Button (Full width) with the UPI Logo
            Button(
                onClick = { uriHandler.openUri(DonationConfig.upiIntentUrl) },
                modifier = Modifier.fillMaxWidth().height(52.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color.White,
                    contentColor = Color.Black
                ),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp)
            ) {
                Image(
                    painter = painterResource(Res.drawable.bhim_upi_),
                    contentDescription = "UPI",
                    modifier = Modifier.height(24.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(stringResource(Res.string.support_dev_upi), fontWeight = FontWeight.Bold)
            }
            
            Spacer(modifier = Modifier.height(12.dp))
            
            // 2. Buy Me a Coffee & Ko-fi (Row)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Buy Me a Coffee Image Badge (Disabled per request)
                /* 
                Image(
                    painter = painterResource(Res.drawable.bmc_button),
                    contentDescription = "Buy Me a Coffee",
                    modifier = Modifier
                        .weight(1f)
                        .height(48.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .clickable { uriHandler.openUri(DonationConfig.BUY_ME_A_COFFEE_URL) },
                    contentScale = androidx.compose.ui.layout.ContentScale.Fit
                )
                */
                // Ko-fi Image Badge
                Image(
                    painter = painterResource(Res.drawable.support_me_on_kofi_badge_beige),
                    contentDescription = "Ko-fi",
                    modifier = Modifier
                        .weight(1f)
                        .height(48.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .clickable { uriHandler.openUri(DonationConfig.KO_FI_URL) },
                    contentScale = androidx.compose.ui.layout.ContentScale.Fit
                )
            }
            
            Spacer(modifier = Modifier.height(24.dp))
            HorizontalDivider()
            
            // 3. Expandable QR Code Section
            TextButton(
                onClick = { showQrCodes = !showQrCodes },
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
            ) {
                Icon(Icons.Filled.QrCode, contentDescription = null, modifier = Modifier.size(20.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text(stringResource(Res.string.support_dev_show_qr), fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.weight(1f))
                Icon(
                    imageVector = if (showQrCodes) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                    contentDescription = null
                )
            }
            
            if (showQrCodes) {
                Spacer(modifier = Modifier.height(16.dp))
                
                // Tabs for QR codes
                ScrollableTabRow(
                    selectedTabIndex = selectedQrIndex,
                    containerColor = Color.Transparent,
                    divider = {},
                    edgePadding = 0.dp
                ) {
                    DonationConfig.availableQrCodes.forEachIndexed { index, option ->
                        Tab(
                            selected = selectedQrIndex == index,
                            onClick = { selectedQrIndex = index },
                            text = { Text(option.title, fontWeight = if (selectedQrIndex == index) FontWeight.Bold else FontWeight.Normal) }
                        )
                    }
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Display the selected QR code
                val selectedQr = DonationConfig.availableQrCodes[selectedQrIndex]
                Card(
                    modifier = Modifier.size(200.dp),
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.cardColors(containerColor = Color.White)
                ) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Image(
                            painter = painterResource(selectedQr.imageRes),
                            contentDescription = selectedQr.title,
                            modifier = Modifier.padding(16.dp).fillMaxSize(),
                            contentScale = androidx.compose.ui.layout.ContentScale.Fit
                        )
                    }
                }
                
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = stringResource(Res.string.support_dev_scan_qr, selectedQr.title),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}
